import React, { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import Modal from '../../../shared/components/ui/Modal';
import { getDueList } from '../../services/api/implementation/impl';
import InfoTip from '../../../shared/components/ui/InfoTip';
import AmountHint from '../../../shared/components/ui/AmountHint';
import { inrLakhCrore } from '../../utils/inrLakhCrore';
import { DUE_BUCKET_HELP, DUE_BUCKET_SCOPE_NOTE } from '../../utils/invoiceHelp';

export const DUE_BUCKET_LABELS = {
  overdue: 'Overdue',
  due: 'Due',
  pending: 'Total Pending',
  uninvoiced: 'Uninvoiced',
  late_fee: 'Pending Late Fee'
};

const HELP = {
  overdue: 'Invoices past their due month and still unpaid. Most overdue first.',
  due: 'Invoiced and not yet overdue. Earliest due date first.',
  pending: 'Overdue plus due: everything invoiced and unpaid.',
  uninvoiced:
    'Sale value not invoiced yet — usually milestones not reached. Never-invoiced deals first: check their stages are unlocked.',
  late_fee: 'Late fees charged and not yet paid.'
};

const PAGE_SIZE = 25;

// Per-deal split columns; the one matching the open bucket is already the
// highlighted amount column, so it is not repeated.
const SPLIT_COLUMNS = [
  { key: 'overdue', label: 'Overdue', tone: 'text-red-700' },
  { key: 'due', label: 'Due', tone: 'text-amber-700' },
  { key: 'uninvoiced', label: 'Uninvoiced', tone: 'text-slate-600' }
];

const HeadWithTip = ({ children, help, right }) => (
  <th className={`px-3 py-2 font-medium ${right ? 'text-right' : ''}`}>
    <span className={`inline-flex items-center gap-1 ${right ? 'justify-end' : ''}`}>
      {children}
      <InfoTip help={help} size={12} />
    </span>
  </th>
);

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const day = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

/**
 * The deals behind one summary card. Totals come from the same calculation as
 * the card (meta.bucketTotal), so the list always adds up to the card.
 */
const DueBucketListModal = ({ bucket, projectId, blockId, onClose, onOpenDeal }) => {
  const [page, setPage] = useState(1);
  const [neverInvoiced, setNeverInvoiced] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [bucket, projectId, blockId, neverInvoiced]);

  const { data, isFetching, isError } = useQuery({
    queryKey: ['dueList', bucket, projectId, blockId, neverInvoiced, page],
    queryFn: () =>
      getDueList({ bucket, projectId, blockId, neverInvoiced, page, limit: PAGE_SIZE }),
    placeholderData: keepPreviousData
  });

  const rows = data?.data ?? [];
  const meta = data?.meta ?? {};
  const totalPages = Math.max(1, Math.ceil((meta.total || 0) / PAGE_SIZE));
  const label = DUE_BUCKET_LABELS[bucket] || 'Deals';
  const splitColumns = SPLIT_COLUMNS.filter((c) => c.key !== bucket);

  const amountOf = (r) =>
    bucket === 'overdue'
      ? r.overdue
      : bucket === 'due'
        ? r.due
        : bucket === 'pending'
          ? r.pending
          : bucket === 'uninvoiced'
            ? r.uninvoiced
            : r.lateFee;

  return (
    <Modal name={`${label} — deals`} width={80} onClose={onClose}>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="flex items-center gap-1 text-sm text-gray-600">
              {HELP[bucket]}
              <InfoTip help={DUE_BUCKET_HELP[bucket]} />
            </p>
            <p className="mt-1 text-sm font-semibold text-gray-900">
              {meta.total ?? 0} {meta.total === 1 ? 'deal' : 'deals'} ·{' '}
              <AmountHint value={meta.bucketTotal}>{inr(meta.bucketTotal)}</AmountHint>
              {projectId ? '' : ' · all projects'}
            </p>
          </div>
          {bucket === 'uninvoiced' ? (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={neverInvoiced}
                onChange={(e) => setNeverInvoiced(e.target.checked)}
              />
              Never invoiced only
            </label>
          ) : null}
        </div>

        <div className="max-h-[60vh] overflow-auto rounded-md border border-gray-200">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="sticky top-0 bg-gray-50 text-xs text-gray-600">
              <tr>
                <th className="px-3 py-2 font-medium">Unit</th>
                <th className="px-3 py-2 font-medium">Buyer</th>
                <th className="px-3 py-2 font-medium">Project / Block</th>
                <HeadWithTip help={DUE_BUCKET_HELP[bucket]} right>
                  {label}
                </HeadWithTip>
                {bucket === 'overdue' || bucket === 'pending' ? (
                  <th className="px-3 py-2 font-medium">Oldest due · days late</th>
                ) : null}
                {bucket === 'due' ? <th className="px-3 py-2 font-medium">Next due</th> : null}
                {splitColumns.map((c) => (
                  <HeadWithTip key={c.key} help={DUE_BUCKET_HELP[c.key]} right>
                    {c.label}
                  </HeadWithTip>
                ))}
                <HeadWithTip help={DUE_BUCKET_HELP.sale_price} right>
                  Sale price
                </HeadWithTip>
                <th className="px-3 py-2 font-medium">Closed</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.dealId}
                  onClick={() => onOpenDeal(r.dealId)}
                  className="cursor-pointer border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-900">
                    {r.unitName || '—'}
                    {r.neverInvoiced ? (
                      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                        Never invoiced
                        <InfoTip help={DUE_BUCKET_HELP.never_invoiced} size={11} />
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-gray-700">{r.buyerName || '—'}</td>
                  <td className="px-3 py-2 text-gray-600">
                    {[r.projectName, r.blockName].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums text-gray-900">
                    <span title={inrLakhCrore(amountOf(r))}>{inr(amountOf(r))}</span>
                  </td>
                  {bucket === 'overdue' || bucket === 'pending' ? (
                    <td className="px-3 py-2 text-gray-700">
                      {r.oldestOverdueDueDate
                        ? `${day(r.oldestOverdueDueDate)} · ${r.daysOverdue}d`
                        : '—'}
                    </td>
                  ) : null}
                  {bucket === 'due' ? <td className="px-3 py-2 text-gray-700">{day(r.nextDueDate)}</td> : null}
                  {splitColumns.map((c) => (
                    <td key={c.key} className={`px-3 py-2 text-right tabular-nums ${c.tone}`}>
                      {inr(r[c.key])}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700">{inr(r.salePrice)}</td>
                  <td className="px-3 py-2 text-gray-600">{day(r.closedAt)}</td>
                </tr>
              ))}
              {!rows.length ? (
                <tr>
                  <td colSpan={11} className="px-3 py-8 text-center text-gray-500">
                    {isFetching ? 'Loading…' : isError ? 'Could not load the list.' : 'No deals in this bucket.'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {totalPages > 1 ? (
          <div className="flex items-center justify-end gap-2 text-sm">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded border border-gray-300 px-3 py-1 disabled:opacity-40">
              Previous
            </button>
            <span className="text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border border-gray-300 px-3 py-1 disabled:opacity-40">
              Next
            </button>
          </div>
        ) : null}
        <p className="text-xs text-gray-500">
          Click a deal to open its invoices. {DUE_BUCKET_SCOPE_NOTE}
        </p>
      </div>
    </Modal>
  );
};

export default DueBucketListModal;
